// Implements: F-SHR-003, F-SHR-005, F-SHR-008, F-UIX-007
/**
 * The share control of the mockup in the Lienzo editor (UX-06): the "Compartir" button right after
 * the preview tabs, and the popover it opens to create a link, see it once, and list and revoke
 * the mockup's links.
 *
 * **Who sees it** (F-SHR-005): nothing is rendered until the mockup is read, and nothing at all
 * unless the server says the person may share (`may_share`, the one decision point of `mockups`;
 * no role is spelled here). While the mockup has no version yet the button is there but disabled,
 * and says why. The links are only asked for once there is something to share.
 *
 * **A popover, not a modal**: anchored to the button, it closes on Escape (the focus goes back to
 * the button) or on a press outside it, and guards the preview frame while open. A creation in
 * flight cannot be closed: its answer carries the only copy of the link.
 *
 * **The link is shown once.** The creation answer is the only one that carries the URL; it lives
 * in this component's state while the popover shows it, and nowhere else: not in the query cache
 * (see `useShareLinks`), not in any browser storage, not in a log. Closing drops it.
 *
 * The page mounts this container with `key` = the mockup id (checklist 14): a draft, a pending
 * creation and a shown link belong to ONE mockup, and an answer that lands after the person moved
 * on has no container left to write into. A double submit is stopped synchronously by a ref.
 *
 * **The expiry starts on 30 days** (owner decision 2026-10-19, as in v3; 7, 30 or 90 days, or
 * "1 año", one year, which sends `never_expires` alone); a value out of the configured range comes back
 * as the server's own `422` sentence. A label is renamed in place in the list (`LinkLabel`), with the
 * label alone in the `PATCH`. **The password travels with the
 * creation** (7.0.2) only while "Proteger con contraseña" is on, so a refused one creates no link;
 * the link is still shown as what the server says it is (protected or not).
 *
 * **"Ver como visitante"** (mockup v3 P8) frames what the link being made ("Enlace sin crear"), the
 * link just made or a listed active link opens, through the visitor preview (C6): no share token, no
 * visit counted. Its overlay is a modal layer, so the popover stays open behind it and the focus
 * comes back to the control that opened it.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { Popover } from "../../../shared/ui/molecules/Popover";
import { Tabs, tabPanelProps } from "../../../shared/ui/molecules/Tabs";
import { useAccessCount } from "../hooks/useAccessCount";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ShareArtifact, ShareLinkView, VisitorPreviewTarget } from "../../../types/sharing";
import type { Schedule } from "../components/CopyLinkButton";
import { SharePanel } from "../components/SharePanel";
import type { PasswordOutcome, ShareDraft, VersionChoices } from "../components/SharePanel";
import { ShareLinkList, linkName } from "../components/ShareLinkList";
import {
  useCreateShareLink,
  useEditShareLink,
  useRevokeShareLink,
  useShareLinks,
  useShareTarget,
  useShareableVersions,
} from "../hooks/useShareLinks";
import { useVisitorPreview } from "../hooks/useVisitorPreview";
import { DEFAULT_EXPIRY, expiryInput } from "../model/expiryDays";
import { absoluteShareUrl } from "../model/shareUrl";
import { visitorTargetOf } from "../model/visitorTarget";
import styles from "./ShareContainer.module.css";
import { VisitorPreviewContainer, visitorKey } from "./VisitorPreviewContainer";

/** The refusals of the create route whose sentence the server writes for people: the cap. */
const CREATE_REFUSAL_CODES = ["CONFLICT"] as const;

/** The refusals of a rename whose sentence the server writes for people: a revoked link. */
const RENAME_REFUSAL_CODES = ["CONFLICT", "VALIDATION"] as const;

/**
 * Open the popover on a preset draft (mockup v3 P6: the diagram drawer's "Compartir" opens it on
 * the diagram artifact of the version it shows). Each press hands a NEW object: the same object
 * handed again (a re-render) opens nothing, so what the person closed stays closed.
 */
export interface ShareOpenRequest {
  readonly artifact: ShareArtifact;
  /** The version the request is about: the current one shares live, an older one fixed on it. */
  readonly versionId: string;
}

export interface ShareContainerProps {
  readonly mockupId: string;
  /** Open the popover on arrival (the gallery card's "Compartir" lands here). */
  readonly openOnMount?: boolean | undefined;
  /** The copy notice's timer; injected so no test sleeps (checklist 16). */
  readonly schedule?: Schedule | undefined;
  /** Mockup v3 P4: the "Personas con acceso" panel; without it the popover has no tabs. */
  readonly peoplePanel?: ReactNode;
  /** Mockup v3 P4: opens the transfer of ownership (the editor's "Más acciones" owns the dialog). */
  readonly onTransfer?: (() => void) | undefined;
  /** Mockup v3 P6: open the popover preset on an artifact and version (`ShareOpenRequest`). */
  readonly openRequest?: ShareOpenRequest | null | undefined;
}

export function ShareContainer({
  mockupId,
  schedule,
  openOnMount = false,
  peoplePanel,
  openRequest = null,
}: ShareContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const hintId = useId();
  const target = useShareTarget(mockupId);
  const mockup = target.data;
  if (mockup?.may_share !== true) return null;
  if (mockup.current_version_id === null) {
    return (
      <>
        <Button variant="secondary" disabled describedBy={hintId}>
          <Icon name="link" size="sm" />
          {t("sharing.open")}
        </Button>
        <span id={hintId} className={styles.srOnly}>
          {t("sharing.unavailable")}
        </span>
      </>
    );
  }
  return (
    <ShareSection
      mockupId={mockupId}
      mockupName={mockup.name}
      currentVersionId={mockup.current_version_id}
      schedule={schedule}
      openOnMount={openOnMount}
      peoplePanel={peoplePanel}
      openRequest={openRequest}
    />
  );
}

interface ShareSectionProps {
  readonly mockupId: string;
  readonly mockupName: string;
  readonly currentVersionId: string;
  readonly schedule: Schedule | undefined;
  readonly openOnMount: boolean;
  readonly peoplePanel: ReactNode;
  readonly openRequest: ShareOpenRequest | null;
}

function freshDraft(currentVersionId: string): ShareDraft {
  return {
    artifact: "prototype",
    mode: "live",
    versionId: currentVersionId,
    expiry: DEFAULT_EXPIRY,
    label: "",
    protect: false,
    password: "",
  };
}

/** The draft a request opens: its artifact, live on the current version or fixed on an older one. */
function requestedDraft(currentVersionId: string, request: ShareOpenRequest): ShareDraft {
  const live = request.versionId === currentVersionId;
  return {
    ...freshDraft(currentVersionId),
    artifact: request.artifact,
    mode: live ? "live" : "frozen",
    versionId: request.versionId,
  };
}

type ShareTab = "link" | "people";

function ShareSection({
  mockupId,
  mockupName,
  currentVersionId,
  schedule,
  openOnMount,
  peoplePanel,
  openRequest,
}: ShareSectionProps): ReactElement {
  const { t } = useTranslation();
  // Mockup v3 P4: the tab strip appears only when the page hands a people panel.
  const hasPeople = peoplePanel !== undefined && peoplePanel !== null;
  const [tab, setTab] = useState<ShareTab>("link");
  const accessCount = useAccessCount(mockupId, hasPeople);
  const tabsId = `share-tabs-${mockupId}`;
  const links = useShareLinks(mockupId);
  const create = useCreateShareLink();
  const revoke = useRevokeShareLink();
  const edit = useEditShareLink();
  const visitor = useVisitorPreview();
  const [open, setOpen] = useState(openOnMount);
  const [draft, setDraft] = useState<ShareDraft>(() => freshDraft(currentVersionId));
  const versions = useShareableVersions(mockupId, open && draft.mode === "frozen");
  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [passwordOutcome, setPasswordOutcome] = useState<PasswordOutcome>({ status: "none" });
  const [revoking, setRevoking] = useState<ShareLinkView | null>(null);
  const [revokePending, setRevokePending] = useState<string | null>(null);
  const [revokeRefusal, setRevokeRefusal] = useState<string | undefined>(undefined);
  const titleId = `share-title-${mockupId}`;
  const openerId = `share-open-${mockupId}`;
  const opener = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef(false);
  // A new open request (v3 P6) opens the popover on its draft, adjusted while rendering so the old
  // draft is never painted. One in flight is left alone: its answer carries the only copy of a link.
  const [seenRequest, setSeenRequest] = useState<ShareOpenRequest | null>(null);
  if (openRequest !== null && openRequest !== seenRequest) {
    setSeenRequest(openRequest);
    if (!pending) {
      setCreatedUrl(null);
      setPasswordOutcome({ status: "none" });
      setRefusal(undefined);
      setDraft(requestedDraft(currentVersionId, openRequest));
      setTab("link");
      setOpen(true);
    }
  }

  useEffect(() => {
    if (open || !returnFocus.current) return;
    returnFocus.current = false;
    opener.current?.focus();
  }, [open]);

  const reset = (): void => {
    setCreatedUrl(null);
    setPasswordOutcome({ status: "none" });
    setRefusal(undefined);
    setDraft(freshDraft(currentVersionId));
  };

  // A creation in flight cannot be closed: its answer carries the only copy of the link.
  const close = (focusBack: boolean): void => {
    if (submitting.current) return;
    returnFocus.current = focusBack;
    setOpen(false);
    reset();
  };

  const submit = async (): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    setPending(true);
    setRefusal(undefined);
    const frozen = draft.mode === "frozen";
    const password = draft.protect ? draft.password : "";
    const outcome = await create(mockupId, {
      artifact: draft.artifact,
      mode: draft.mode,
      version_id: frozen ? draft.versionId : null,
      ...expiryInput(draft.expiry),
      label: draft.label,
      // 7.0.2: the password travels with the creation, so a refused one creates no link.
      ...(password === "" ? {} : { password }),
    });
    submitting.current = false;
    setPending(false);
    if (!outcome.ok) {
      setRefusal(
        outcome.error.status === 503
          ? t("sharing.disabled")
          : userMessage(outcome.error, t("sharing.failed"), CREATE_REFUSAL_CODES),
      );
      return;
    }
    if (password !== "") {
      // What the server says the link is, never what was typed: a link without it is said so.
      setPasswordOutcome({ status: outcome.hasPassword ? "set" : "unprotected" });
    }
    setCreatedUrl(absoluteShareUrl(outcome.url, window.location.origin));
  };

  const confirmRevoke = async (link: ShareLinkView): Promise<void> => {
    setRevoking(null);
    setRevokePending(link.id);
    setRevokeRefusal(undefined);
    const outcome = await revoke(mockupId, link.id);
    setRevokePending(null);
    if (!outcome.ok) setRevokeRefusal(userMessage(outcome.error, t("sharing.revoke.failed")));
  };

  // Mockup v3 P8: the label alone (C4); a refusal is the server's sentence when it writes one.
  const rename = async (link: ShareLinkView, label: string): Promise<string | null> => {
    const outcome = await edit(mockupId, link.id, { label });
    return outcome.ok ? null : userMessage(outcome.error, t("sharing.label.failed"), RENAME_REFUSAL_CODES);
  };

  // What the form is about to share, or has just shared: the version a fixed link keeps.
  const draftTarget = (): VisitorPreviewTarget => ({
    mockupId,
    artifact: draft.artifact,
    versionId: draft.mode === "frozen" ? draft.versionId : null,
    linkId: null,
    label:
      createdUrl === null
        ? t("sharing.visitor.unsaved")
        : draft.label.trim() === ""
          ? t("sharing.list.noLabel")
          : draft.label.trim(),
  });

  const choices: VersionChoices = versions.isError
    ? { status: "error" }
    : versions.data === undefined
      ? { status: "loading" }
      : {
          status: "ready",
          choices: [...versions.data]
            .sort((left, right) => right.sequence_number - left.sequence_number)
            .map((node) => ({
              id: node.id,
              label:
                node.id === currentVersionId
                  ? t("sharing.version.current", { n: node.sequence_number })
                  : t("sharing.version.option", { n: node.sequence_number }),
            })),
        };

  const list = (
    <>
      {revokeRefusal === undefined ? null : (
        <p role="alert" className={styles.refusal}>
          {revokeRefusal}
        </p>
      )}
      {links.isError ? (
        <p className={styles.refusal}>{t("sharing.list.failed")}</p>
      ) : links.data === undefined ? (
        <p className={styles.hint}>{t("sharing.list.loading")}</p>
      ) : (
        <ShareLinkList
          links={links.data}
          pendingId={revokePending}
          onRevoke={(link) => {
            // The confirmation is a modal over the page; the popover steps aside and comes back.
            setOpen(false);
            setRevoking(link);
          }}
          onRename={rename}
          onVisitor={(link) => {
            visitor.open(visitorTargetOf(link, linkName(link, t("sharing.list.noLabel"))));
          }}
        />
      )}
    </>
  );

  const linkPanel = (
    <SharePanel
      titleId={titleId}
      idBase={`share-${mockupId}`}
      mockupName={mockupName}
      draft={draft}
      onChange={(patch) => {
        setDraft((current) => ({ ...current, ...patch }));
      }}
      versions={choices}
      refusal={refusal}
      pending={pending}
      createdUrl={createdUrl}
      passwordOutcome={passwordOutcome}
      onSubmit={() => {
        void submit();
      }}
      onAgain={reset}
      onClose={() => {
        close(true);
      }}
      onVisitor={() => {
        visitor.open(draftTarget());
      }}
      links={list}
      activeCount={links.data === undefined ? null : links.data.filter((link) => link.state === "active").length}
      schedule={schedule}
    />
  );

  return (
    <>
      <Button
        variant="secondary"
        id={openerId}
        buttonRef={opener}
        hasPopup="dialog"
        expanded={open}
        onClick={() => {
          if (open) close(false);
          else {
            // The current version may have moved since the page mounted (a promote): start from it.
            setDraft(freshDraft(currentVersionId));
            setOpen(true);
          }
        }}
      >
        <Icon name="link" size="sm" />
        {t("sharing.open")}
      </Button>
      <Popover
        open={open}
        onClose={() => {
          close(false);
        }}
        anchorRef={opener}
        labelledBy={hasPeople && tab === "people" ? `${tabsId}-tab-people` : titleId}
        width="sm"
      >
        {hasPeople ? (
          <Tabs
            label={t("sharing.tab.label")}
            idBase={tabsId}
            items={[
              { id: "link", label: t("sharing.tab.link") },
              {
                id: "people",
                label: accessCount === null ? t("sharing.tab.people") : t("sharing.tab.peopleCount", { count: accessCount }),
              },
            ]}
            selected={tab}
            onSelect={(id) => {
              setTab(id === "people" ? "people" : "link");
            }}
          />
        ) : null}
        {!hasPeople ? (
          linkPanel
        ) : tab === "people" ? (
          <div {...tabPanelProps(tabsId, "people")}>{peoplePanel}</div>
        ) : (
          // Not a tab stop: the link panel opens on focusable controls, and a click on its text must
          // leave the focus where it was (Escape then closes the popover, never the turn in flight).
          <div {...tabPanelProps(tabsId, "link")} tabIndex={undefined}>
            {linkPanel}
          </div>
        )}
      </Popover>
      {visitor.target === null ? null : (
        <VisitorPreviewContainer key={visitorKey(visitor.target)} target={visitor.target} onClose={visitor.close} />
      )}
      <ConfirmDialog
        open={revoking !== null}
        title={t("sharing.revoke.title")}
        body={t("sharing.revoke.body")}
        confirmLabel={t("sharing.revoke")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={() => {
          if (revoking !== null) void confirmRevoke(revoking);
          setOpen(true);
        }}
        onCancel={() => {
          setRevoking(null);
          setOpen(true);
        }}
      />
    </>
  );
}
